import { useQueries } from '@tanstack/react-query'
import { useMemo } from 'react'

import type { ActivationGroup } from '@/features/activationGroup/types'
import { useBiddingContext } from '@/features/bidding/contexts/BiddingContext'
import {
  CALCULATE_BASIS_FOR_NEW_BID_API_ID,
  calculateBasisForNewBid,
} from '@/features/bidding/endpoints/bidsCalculations'
import { useErrorHandler } from '@/features/bidding/hooks/useErrorHandler'
import type { BidVolumes } from '@/features/bidding/types/bid'
import type { GroupedBidParams } from '@/features/bidding/utils/groupedBidParams/buildBiddingLinks'
import { isValueStackingSupported } from '@/features/bidding/utils/isValueStackingSupported'
import { logMessageInCoralogix } from '@/features/coralogix/utils/sendExceptions'

export type UseBidOfferBidBasisResult = {
  bidBasisByActivationGroup: Map<string, BidVolumes>
  isLoading: boolean
}

/**
 * Hook to fetch bidBasis data for all activation groups in parallel.
 * Creates a Map keyed by activationGroupUuid for efficient lookup.
 *
 * @param groupParams - The grouped bid parameters containing deliveryDay, marketProgram, portfolioCode, and bidType.
 * @param eligibleActivationGroups - Array of activation groups to fetch bidBasis for
 * @returns An object containing a Map of bidBasis by activationGroupUuid and loading state
 *
 * @example
 * ```tsx
 * const { bidBasisByActivationGroup, isLoading } = useBidOfferBidBasis(groupParams, activationGroups)
 *
 * if (isLoading) return <Spinner />
 *
 * const bidBasis = bidBasisByActivationGroup.get(activationGroupUuid)
 * ```
 */
export function useBidOfferBidBasis(
  groupParams: GroupedBidParams,
  eligibleActivationGroups: ActivationGroup[],
): UseBidOfferBidBasisResult {
  const { selectedCountry } = useBiddingContext()

  const queryResults = useQueries({
    queries: eligibleActivationGroups.map((activationGroup) => {
      return {
        queryKey: [
          CALCULATE_BASIS_FOR_NEW_BID_API_ID,
          {
            date: groupParams.deliveryDay,
            portfolioCode: groupParams.portfolioCode,
            marketProgram: groupParams.marketProgram,
            activationGroupUuid: activationGroup.uuid,
            bidType: groupParams.bidType,
            selectedCountry,
          },
        ],
        queryFn: () => {
          const isValueStackingEnabled = isValueStackingSupported(groupParams.marketProgram, selectedCountry)
          return calculateBasisForNewBid(
            groupParams.deliveryDay,
            activationGroup.uuid,
            groupParams.bidType,
            selectedCountry,
            isValueStackingEnabled,
          )
        },
        enabled: true,
      }
    }),
  })

  useErrorHandler(queryResults.some((result) => result.isError))

  const bidBasisByActivationGroup = useMemo(() => {
    const map = new Map<string, BidVolumes>()

    queryResults.forEach((result, index) => {
      const activationGroupUuid = eligibleActivationGroups[index].uuid

      if (result.data) {
        map.set(activationGroupUuid, result.data)
      } else if (!result.isLoading && !result.isError) {
        // Query completed but returned no data - log for debugging
        const noDataMessage =
          `No bid basis data for activation group ${activationGroupUuid}. ` +
          `Query status: loading=${result.isLoading}, error=${result.isError}, data=${result.data}`
        logMessageInCoralogix(noDataMessage)
      }
    })

    return map
  }, [queryResults, eligibleActivationGroups])

  const isLoading = queryResults.some((result) => result.isLoading)

  // Memoize the return object to prevent unnecessary recalculations in dependent hooks
  return useMemo(
    () => ({
      bidBasisByActivationGroup,
      isLoading,
    }),
    [bidBasisByActivationGroup, isLoading],
  )
}
